import React, {
  createContext,
  useCallback,
  useState,
  useContext, useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  favoriteFilters,
  getFilters,
  unfavoriteFilters,
} from 'shared/api/user_profile';
import { AuthContext } from 'shared/providers/AuthProvider';
import { useNotification, useRequest } from 'shared/hooks';

export const FilterContext = createContext({
  getFavoriteFilters: () => '',
  setFavoriteFilters: () => {},
  clearFavoriteFilters: () => {},
  clearModuleFilters: () => {},
  filters: null,
});

export function FilterRedirect(path, savedFilters = '') {
  const { search } = useLocation();
  const notification = useNotification();
  const history = useHistory();
  // eslint-disable-next-line max-len
  const savedFiltersAndNoSearchParams = new URLSearchParams(search).toString() === '' && savedFilters !== '';
  if (savedFiltersAndNoSearchParams) {
    notification.pushInfo('Saved Filters Applied');
    history.replace(`${path}${savedFilters}`);
    return true;
  }
  return false;
}

function withFilters(WrappedComponent, moduleName) {
  const emptyFilters = { filters: [] };

  function FilterProvider(props) {
    const [savedFilters, setSavedFilters] = useState(undefined);
    const { request } = useRequest();
    const { push: pushHistory } = useHistory();
    const { user: { id: userId } } = useContext(AuthContext);
    const notification = useNotification();

    const setFavoriteFilters = useCallback(async (newFilters) => {
      const result = await request(favoriteFilters(userId, moduleName, newFilters));
      setSavedFilters(result);
      notification.pushInfo('Favorite Filters Saved');
    }, [notification, request, userId]);

    const getFavoriteFilters = useCallback(async () => {
      const result = await request(getFilters(userId, moduleName));
      if (result) {
        setSavedFilters(result);
        return;
      }
      setSavedFilters(emptyFilters);
    }, [request, userId]);

    const clearFavoriteFilters = useCallback(async (filterToRemove) => {
      const result = await request(unfavoriteFilters(userId, moduleName, filterToRemove));
      setSavedFilters(result);
      pushHistory('?');
      notification.pushInfo('Favorite Filters Cleared');
    }, [notification, pushHistory, request, userId]);

    const filterProviderValue = useMemo(() => ({
      setFavoriteFilters,
      getFavoriteFilters,
      clearFavoriteFilters,
      savedFilters,
    }), [
      clearFavoriteFilters,
      savedFilters,
      getFavoriteFilters,
      setFavoriteFilters,
    ]);

    return (
      <FilterContext.Provider value={filterProviderValue}>
        <WrappedComponent {...props} />
      </FilterContext.Provider>
    );
  }

  return FilterProvider;
}

export function prepareFilters(title) {
  return (component) => withFilters(component, title);
}
